import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon
} from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import {
  fetchClientTreatmentPlans,
  deleteTreatmentPlan
} from "../../store/thunks/treatmentPlanThunk";
import {
  fetchClientProgressSummaries,
  deleteProgressSummary
} from "../../store/thunks/progressSummaryThunk";
import TreatmentPlanForm from "../../applicationUi/components/TreatmentPlanForm";
import ProgressSummaryForm from "../../applicationUi/components/ProgressSummaryForm";
import ConfirmDialog from "../../applicationUi/components/ConfirmDialog";
import { downloadTreatmentPlanPDF } from "../../utils/treatmentPlanPdfGenerator";
import { downloadProgressSummaryPDF } from "../../utils/progressSummaryPdfGenerator";

const ClientTreatmentPlans = ({ clientId }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const client = useSelector((state) => state.clients.selectedClient);
  const {
    clientTreatmentPlans,
    loading: treatmentPlansLoading,
    deleteLoading: treatmentPlanDeleteLoading,
    error: treatmentPlansError
  } = useSelector((state) => state.treatmentPlan);
  const {
    clientProgressSummaries,
    loading: progressSummariesLoading,
    deleteLoading: progressSummaryDeleteLoading,
    error: progressSummariesError
  } = useSelector((state) => state.progressSummary);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingSummary, setEditingSummary] = useState(null);
  const [showCreateTreatmentPlan, setShowCreateTreatmentPlan] = useState(false);
  const [showCreateProgressSummary, setShowCreateProgressSummary] = useState(false);
  const [showEditTreatmentPlan, setShowEditTreatmentPlan] = useState(false);
  const [showEditProgressSummary, setShowEditProgressSummary] = useState(false);
  const [showViewPlanModal, setShowViewPlanModal] = useState(false);
  const [showViewSummaryModal, setShowViewSummaryModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Load treatment plans and progress summaries when component mounts or clientId changes
  useEffect(() => {
    if (clientId) {
      console.log(`[Client Treatment Plans] Loading treatment plans and progress summaries for client ${clientId}`);
      dispatch(fetchClientTreatmentPlans({ clientId, page: 1, limit: 100 }));
      dispatch(fetchClientProgressSummaries({ clientId, page: 1, limit: 100 }));
    }
  }, [dispatch, clientId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewPlan = (plan) => {
    console.log(`[Client Treatment Plans] Viewing plan:`, plan._id);
    setSelectedPlan(plan);
    setShowViewPlanModal(true);
  };

  const handleViewSummary = (summary) => {
    console.log(`[Client Treatment Plans] Viewing summary:`, summary._id);
    setSelectedSummary(summary);
    setShowViewSummaryModal(true);
  };

  const handleEditPlan = (plan) => {
    console.log(`[Client Treatment Plans] Editing plan:`, plan._id);
    setEditingPlan(plan);
    setShowEditTreatmentPlan(true);
  };

  const handleEditSummary = (summary) => {
    console.log(`[Client Treatment Plans] Editing summary:`, summary._id);
    setEditingSummary(summary);
    setShowEditProgressSummary(true);
  };

  const handleDeletePlan = (plan) => {
    console.log(`[Client Treatment Plans] Confirming delete for plan:`, plan._id);
    setConfirmDelete({ type: 'plan', item: plan });
  };

  const handleDeleteSummary = (summary) => {
    console.log(`[Client Treatment Plans] Confirming delete for summary:`, summary._id);
    setConfirmDelete({ type: 'summary', item: summary });
  };

  const handleDownloadPlanPDF = async (plan) => {
    try {
      console.log(`[Client Treatment Plans] Downloading treatment plan PDF for:`, plan.clientName);
      await downloadTreatmentPlanPDF(plan);
      toast.success("Treatment plan PDF downloaded successfully");
    } catch (error) {
      console.error(`[Client Treatment Plans] Error downloading treatment plan PDF:`, error);
      toast.error("Failed to download treatment plan PDF");
    }
  };

  const handleDownloadSummaryPDF = async (summary) => {
    try {
      console.log(`[Client Treatment Plans] Downloading progress summary PDF for:`, summary.clientName);
      await downloadProgressSummaryPDF(summary);
      toast.success("Progress summary PDF downloaded successfully");
    } catch (error) {
      console.error(`[Client Treatment Plans] Error downloading progress summary PDF:`, error);
      toast.error("Failed to download progress summary PDF");
    }
  };

  const confirmDeleteItem = async () => {
    if (!confirmDelete) return;

    const { type, item } = confirmDelete;

    try {
      if (type === 'plan') {
        console.log(`[Client Treatment Plans] Deleting plan:`, item._id);
        await dispatch(deleteTreatmentPlan(item._id)).unwrap();
        toast.success("Treatment plan deleted successfully");
        dispatch(fetchClientTreatmentPlans({ clientId, page: 1, limit: 100 }));
      } else if (type === 'summary') {
        console.log(`[Client Treatment Plans] Deleting summary:`, item._id);
        await dispatch(deleteProgressSummary(item._id)).unwrap();
        toast.success("Progress summary deleted successfully");
        dispatch(fetchClientProgressSummaries({ clientId, page: 1, limit: 100 }));
      }
      setConfirmDelete(null);
    } catch (error) {
      console.error(`[Client Treatment Plans] Error deleting ${type}:`, error);
      toast.error(`Failed to delete ${type === 'plan' ? 'treatment plan' : 'progress summary'}`);
    }
  };

  const handlePlanFormSave = () => {
    dispatch(fetchClientTreatmentPlans({ clientId, page: 1, limit: 100 }));
    setShowCreateTreatmentPlan(false);
    setShowEditTreatmentPlan(false);
    setEditingPlan(null);
  };

  const handleSummaryFormSave = () => {
    dispatch(fetchClientProgressSummaries({ clientId, page: 1, limit: 100 }));
    setShowCreateProgressSummary(false);
    setShowEditProgressSummary(false);
    setEditingSummary(null);
  };

  const loading = treatmentPlansLoading || progressSummariesLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-2 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  const treatmentPlans = clientTreatmentPlans?.treatmentPlans?.docs || [];
  const progressSummaries = clientProgressSummaries?.progressSummaries?.docs || [];

  // Combine and sort by date (most recent first)
  const allDocuments = [
    ...treatmentPlans.map(plan => ({ ...plan, type: 'plan' })),
    ...progressSummaries.map(summary => ({ ...summary, type: 'summary' }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="space-y-4">
      {/* Header with Dropdown Create Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Treatment Plans & Progress Reports</h3>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-sm hover:bg-indigo-700 transition-colors">
            <PlusIcon className="h-4 w-4 mr-2" />
            New Document
            <ChevronDownIcon className="h-4 w-4 ml-2" />
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowCreateTreatmentPlan(true);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-3 text-indigo-600" />
                  Treatment Plan
                </button>
                <button
                  onClick={() => {
                    setShowCreateProgressSummary(true);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                  <ClipboardDocumentListIcon className="h-5 w-5 mr-3 text-green-600" />
                  Progress Summary
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documents List */}
      {allDocuments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a treatment plan or progress summary for this client.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {allDocuments.map((doc) => (
            <div
              key={doc._id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Header Info */}
                  <div className="flex items-center space-x-4 mb-3">
                    {doc.type === 'plan' ? (
                      <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
                    ) : (
                      <ClipboardDocumentListIcon className="h-6 w-6 text-green-600" />
                    )}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                      doc.type === 'plan' ? 'bg-indigo-100 text-indigo-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {doc.type === 'plan' ? 'Treatment Plan' : 'Progress Summary'}
                    </span>
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(doc.date).toLocaleDateString()}
                      </span>
                    </div>
                    {doc.clientId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Linked Client
                      </span>
                    )}
                  </div>

                  {/* Document Details */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {doc.type === 'plan' ? (
                      <>
                        <div>
                          <div className="text-sm text-gray-500">Speech-Language Pathologist</div>
                          <div className="text-sm font-medium">{doc.speechLanguagePathologist || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Goals</div>
                          <div className="text-sm font-medium">
                            {doc.goals?.length || 0} goal(s)
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Clinician(s)</div>
                          <div className="text-sm font-medium">{doc.clinicians || 'N/A'}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <div className="text-sm text-gray-500">Supervising SLP</div>
                          <div className="text-sm font-medium">{doc.supervisingSLP || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Session Number</div>
                          <div className="text-sm font-medium">{doc.sessionNumber || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Goals Assessed</div>
                          <div className="text-sm font-medium">
                            {doc.goals?.length || 0} goal(s)
                          </div>
                        </div>
                      </>
                    )}
                    <div>
                      <div className="text-sm text-gray-500">Created By</div>
                      <div className="text-sm font-medium">
                        {doc.createdBy?.firstName} {doc.createdBy?.lastName}
                      </div>
                    </div>
                  </div>

                  {/* Goals Preview (only for treatment plans) */}
                  {doc.type === 'plan' && doc.goals && doc.goals.length > 0 && (
                    <div className="mb-4">
                      <div className="text-sm text-gray-500 mb-2">Goals:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {doc.goals.slice(0, 3).map((goal, idx) => (
                          <li key={goal.id || idx} className="text-sm text-gray-700 line-clamp-1">
                            <div
                              className="inline prose prose-sm [&>p]:inline [&>p]:mb-0 [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                              dangerouslySetInnerHTML={{ __html: goal.description.substring(0, 100) }}
                            />
                          </li>
                        ))}
                        {doc.goals.length > 3 && (
                          <li className="text-sm text-gray-500 italic">
                            +{doc.goals.length - 3} more goal(s)
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => doc.type === 'plan' ? handleViewPlan(doc) : handleViewSummary(doc)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    title="View Details">
                    <EyeIcon className="h-5 w-5" />
                  </button>

                  <button
                    onClick={() => doc.type === 'plan' ? handleDownloadPlanPDF(doc) : handleDownloadSummaryPDF(doc)}
                    className={`p-2 rounded-full ${
                      doc.type === 'plan'
                        ? 'text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100'
                        : 'text-green-400 hover:text-green-600 hover:bg-green-100'
                    }`}
                    title="Download PDF">
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>

                  {/* Only allow editing if user created it or is admin */}
                  {(doc.createdBy?._id === user._id || user.role?.includes('admin')) && (
                    <button
                      onClick={() => doc.type === 'plan' ? handleEditPlan(doc) : handleEditSummary(doc)}
                      className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-full"
                      title="Edit">
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  )}

                  {/* Only allow deletion if user created it or is admin */}
                  {(doc.createdBy?._id === user._id || user.role?.includes('admin')) && (
                    <button
                      onClick={() => doc.type === 'plan' ? handleDeletePlan(doc) : handleDeleteSummary(doc)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full"
                      title="Delete">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Treatment Plan Modal */}
      {showCreateTreatmentPlan && (
        <TreatmentPlanForm
          clientId={clientId}
          clientName={client ? `${client.firstName} ${client.lastName}` : ""}
          onClose={() => setShowCreateTreatmentPlan(false)}
          onSave={handlePlanFormSave}
        />
      )}

      {/* Create Progress Summary Modal */}
      {showCreateProgressSummary && (
        <ProgressSummaryForm
          onClose={() => setShowCreateProgressSummary(false)}
          onSave={handleSummaryFormSave}
        />
      )}

      {/* Edit Treatment Plan Modal */}
      {showEditTreatmentPlan && editingPlan && (
        <TreatmentPlanForm
          editingTreatmentPlan={editingPlan}
          onClose={() => {
            setShowEditTreatmentPlan(false);
            setEditingPlan(null);
          }}
          onSave={handlePlanFormSave}
        />
      )}

      {/* Edit Progress Summary Modal */}
      {showEditProgressSummary && editingSummary && (
        <ProgressSummaryForm
          editingProgressSummary={editingSummary}
          onClose={() => {
            setShowEditProgressSummary(false);
            setEditingSummary(null);
          }}
          onSave={handleSummaryFormSave}
        />
      )}

      {/* View Treatment Plan Modal */}
      {showViewPlanModal && selectedPlan && (
        <TreatmentPlanViewModal
          plan={selectedPlan}
          onClose={() => {
            setShowViewPlanModal(false);
            setSelectedPlan(null);
          }}
        />
      )}

      {/* View Progress Summary Modal */}
      {showViewSummaryModal && selectedSummary && (
        <ProgressSummaryViewModal
          summary={selectedSummary}
          onClose={() => {
            setShowViewSummaryModal(false);
            setSelectedSummary(null);
          }}
        />
      )}

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <ConfirmDialog
          isOpen={true}
          title={`Delete ${confirmDelete.type === 'plan' ? 'Treatment Plan' : 'Progress Summary'}`}
          message={`Are you sure you want to delete this ${confirmDelete.type === 'plan' ? 'treatment plan' : 'progress summary'} for ${confirmDelete.item.clientName}? This action cannot be undone.`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDeleteItem}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

// Treatment Plan View Modal Component
const TreatmentPlanViewModal = ({ plan, onClose }) => {
  const handleDownloadPDF = async () => {
    try {
      console.log(`[Treatment Plan View Modal] Downloading PDF for:`, plan.clientName);
      await downloadTreatmentPlanPDF(plan);
      toast.success("Treatment plan PDF downloaded successfully");
    } catch (error) {
      console.error(`[Treatment Plan View Modal] Error downloading PDF:`, error);
      toast.error("Failed to download treatment plan PDF");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Treatment Plan - {plan.clientName}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-sm hover:bg-indigo-700 transition-colors"
                title="Download PDF">
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="mt-1 text-sm text-gray-900">
                {new Date(plan.date).toLocaleDateString()}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Grade</label>
              <div className="mt-1 text-sm text-gray-900">{plan.grade || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">School</label>
              <div className="mt-1 text-sm text-gray-900">{plan.school || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Speech-Language Pathologist</label>
              <div className="mt-1 text-sm text-gray-900">{plan.speechLanguagePathologist || 'N/A'}</div>
            </div>
          </div>

          {/* Communication Status */}
          {plan.currentCommunicationStatus && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Communication Status</label>
              <div
                className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal"
                dangerouslySetInnerHTML={{ __html: plan.currentCommunicationStatus }}
              />
            </div>
          )}

          {/* Treatment Plan */}
          {plan.treatmentPlan && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Treatment Plan</label>
              <div
                className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal"
                dangerouslySetInnerHTML={{ __html: plan.treatmentPlan }}
              />
            </div>
          )}

          {/* Goals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Treatment Goals</label>
            <div className="space-y-3">
              {plan.goals?.map((goal, index) => (
                <div key={goal.id} className="p-4 border border-gray-200 rounded-md">
                  <div className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium mr-3">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <div
                        className="text-sm text-gray-900 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal"
                        dangerouslySetInnerHTML={{ __html: goal.description }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Progress Summary View Modal Component
const ProgressSummaryViewModal = ({ summary, onClose }) => {
  const handleDownloadPDF = async () => {
    try {
      console.log(`[Progress Summary View Modal] Downloading PDF for:`, summary.clientName);
      await downloadProgressSummaryPDF(summary);
      toast.success("Progress summary PDF downloaded successfully");
    } catch (error) {
      console.error(`[Progress Summary View Modal] Error downloading PDF:`, error);
      toast.error("Failed to download progress summary PDF");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Progress Summary - {summary.clientName}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-sm hover:bg-green-700 transition-colors"
                title="Download PDF">
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="mt-1 text-sm text-gray-900">
                {new Date(summary.date).toLocaleDateString()}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Session Number</label>
              <div className="mt-1 text-sm text-gray-900">{summary.sessionNumber || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Grade</label>
              <div className="mt-1 text-sm text-gray-900">{summary.grade || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">School</label>
              <div className="mt-1 text-sm text-gray-900">{summary.school || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Supervising SLP</label>
              <div className="mt-1 text-sm text-gray-900">{summary.supervisingSLP || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Clinician(s)</label>
              <div className="mt-1 text-sm text-gray-900">{summary.clinicians || 'N/A'}</div>
            </div>
          </div>

          {/* Treatment Plan */}
          {summary.treatmentPlan && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Treatment Plan</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                {summary.treatmentPlan}
              </div>
            </div>
          )}

          {/* Goals and Progress */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Goals and Progress</label>
            <div className="space-y-3">
              {summary.goals?.map((goal, idx) => {
                const progressLevel = goal.achieved ? 'Achieved' :
                  goal.emergingMinimal ? 'Emerging with Minimal Support' :
                  goal.emergingModerate ? 'Emerging with Moderate Support' :
                  goal.emergingMaximum ? 'Emerging with Maximum Support' :
                  goal.notYetTargeted ? 'Not Yet Targeted' : 'N/A';

                const progressColor = goal.achieved ? 'bg-green-100 text-green-800' :
                  goal.emergingMinimal ? 'bg-blue-100 text-blue-800' :
                  goal.emergingModerate ? 'bg-yellow-100 text-yellow-800' :
                  goal.emergingMaximum ? 'bg-orange-100 text-orange-800' :
                  'bg-gray-100 text-gray-800';

                return (
                  <div key={goal.id || idx} className="p-4 border border-gray-200 rounded-md">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 mb-1">{goal.description}</div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${progressColor}`}>
                          {progressLevel}
                        </span>
                      </div>
                    </div>
                    {goal.actions && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Actions:</span> {goal.actions}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Comments */}
          {summary.comments && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Comments</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                {summary.comments}
              </div>
            </div>
          )}

          {/* Next Steps */}
          {summary.nextSteps && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Next Steps</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                {summary.nextSteps}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientTreatmentPlans;
